import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError, money, shortDate, type CarMethod, type CarYear, type VehicleDetail, type VehicleSummary } from "../api";
import { Choice, ErrorNotice, InlineForm, Loading } from "../ui";

const toCents = (text: string): number | null => {
  const clean = text.replace(/[$,\s]/g, "");
  if (!clean) return null;
  const n = Number(clean);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : NaN;
};
const int = (text: string): number | null => (text.trim() === "" ? null : Number.isInteger(Number(text)) ? Number(text) : NaN);
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });
const METHOD_LABEL: Record<CarMethod, string> = { logbook: "Logbook", cents_per_km: "Cents per km" };

// ---- List -------------------------------------------------------------------------------------

export function Cars() {
  const [cars, setCars] = useState<VehicleSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    api.get<VehicleSummary[]>("/api/vehicles").then(setCars).catch(setError);
  }, []);
  return (
    <div className="stack" style={{ "--gap": "24px" } as React.CSSProperties}>
      <div className="row wrap">
        <div>
          <h1>Cars</h1>
          <p className="muted">Cars you use for work: how they're claimed each year, their logbooks and depreciation.</p>
        </div>
        <div className="spacer" />
        <Link to="/cars/new" className="btn primary">+ Add a car</Link>
      </div>
      <ErrorNotice error={error} />
      {!cars ? <Loading /> : cars.length === 0 ? (
        <div className="card flat empty">No cars yet. Add the car you use for work to start claiming it.</div>
      ) : (
        <div className="card list-card list">
          {cars.map((c) => (
            <Link key={c.id} to={`/cars/${c.id}`} className="list-item">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{c.name}{c.disposedOn && <span className="pill" style={{ marginLeft: 8 }}>Sold {shortDate(c.disposedOn)}</span>}</div>
                <div className="muted small">{[c.year, c.make, c.model].filter(Boolean).join(" ") || "—"}{c.registration ? ` · ${c.registration}` : ""}</div>
              </div>
              <div className="muted small" style={{ textAlign: "right" }}>
                {c.methodThisYear ? METHOD_LABEL[c.methodThisYear] : "Method not set"}
                {c.businessPct !== null && <div>{c.businessPct}% business use</div>}
              </div>
              <span aria-hidden className="muted">›</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Add a car --------------------------------------------------------------------------------

export function AddCar() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [rego, setRego] = useState("");
  const [acquiredOn, setAcquiredOn] = useState("");
  const [cost, setCost] = useState("");
  const [odometer, setOdometer] = useState("");
  const [finance, setFinance] = useState<"owned" | "loan" | "lease" | "novated_lease">("owned");
  const [method, setMethod] = useState<CarMethod | null>(null);
  const [opening, setOpening] = useState("");
  const [needsOpening, setNeedsOpening] = useState<{ asAt: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const costCents = toCents(cost);
      const openingCents = toCents(opening);
      if (costCents === null || Number.isNaN(costCents)) throw new Error("Enter what you paid, like 48000");
      if (Number.isNaN(openingCents)) throw new Error("The value must be a number, like 12000");
      const car = await api.post<VehicleDetail>("/api/vehicles", {
        name: name.trim(), make: make.trim() || null, model: model.trim() || null, year: year ? Number(year) : null,
        registration: rego.trim() || null, finance, acquiredOn, costCents, odometerAtPurchase: int(odometer),
        method: method ?? undefined, openingValueCents: openingCents,
      });
      navigate(`/cars/${car.id}`, { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.details?.needs === "openingValueCents") {
        setNeedsOpening({ asAt: e.details.asAt });
        setStep(3);
      } else {
        setError(e);
      }
    } finally {
      setBusy(false);
    }
  };

  const steps = [
    {
      title: "About the car",
      valid: name.trim().length > 0,
      body: (
        <>
          <div className="field"><label htmlFor="car-name">What do you call it?</label><input id="car-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Work car, the Mazda" autoFocus /></div>
          <div className="fields-2">
            <div className="field"><label htmlFor="car-make">Make</label><input id="car-make" type="text" value={make} onChange={(e) => setMake(e.target.value)} /></div>
            <div className="field"><label htmlFor="car-model">Model</label><input id="car-model" type="text" value={model} onChange={(e) => setModel(e.target.value)} /></div>
          </div>
          <div className="fields-2">
            <div className="field"><label htmlFor="car-year">Year</label><input id="car-year" type="number" value={year} onChange={(e) => setYear(e.target.value)} /></div>
            <div className="field"><label htmlFor="car-rego">Registration <span className="muted">(optional)</span></label><input id="car-rego" type="text" value={rego} onChange={(e) => setRego(e.target.value)} /></div>
          </div>
        </>
      ),
    },
    {
      title: "Buying it",
      valid: Boolean(acquiredOn) && Boolean(cost.trim()),
      body: (
        <>
          <div className="fields-2">
            <div className="field"><label htmlFor="car-date">Date you got it</label><input id="car-date" type="date" max={today()} value={acquiredOn} onChange={(e) => setAcquiredOn(e.target.value)} /></div>
            <div className="field"><label htmlFor="car-cost">What you paid</label><input id="car-cost" type="text" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="48000" /><span className="hint">Drive-away price. The tax limit on a car's cost is applied for you.</span></div>
          </div>
          <div className="field" style={{ maxWidth: 260 }}><label htmlFor="car-odo">Odometer when you got it <span className="muted">(optional)</span></label><input id="car-odo" type="number" value={odometer} onChange={(e) => setOdometer(e.target.value)} /></div>
          <div className="question">
            <span className="label">How is it paid for?</span>
            <Choice label="Finance" value={finance} onChange={setFinance} options={[
              { value: "owned", label: "Bought outright" }, { value: "loan", label: "Car loan" },
              { value: "lease", label: "Lease" }, { value: "novated_lease", label: "Novated lease" },
            ]} />
            {finance === "novated_lease" && <div className="notice warn">A novated lease is usually paid through your employer, and you generally can't claim car expenses yourself. Check with your accountant.</div>}
          </div>
        </>
      ),
    },
    {
      title: "How you'll claim it",
      valid: method !== null,
      body: (
        <>
          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            <button type="button" className="tile" aria-pressed={method === "logbook"} onClick={() => setMethod("logbook")}>
              <span className="title">Logbook<span className="tick" aria-hidden>{method === "logbook" ? "✓" : ""}</span></span>
              <span className="muted small">Claim your actual costs (fuel, rego, insurance, servicing, depreciation) × your work-use %. Needs a 12-week logbook, valid for 5 years.</span>
            </button>
            <button type="button" className="tile" aria-pressed={method === "cents_per_km"} onClick={() => setMethod("cents_per_km")}>
              <span className="title">Cents per km<span className="tick" aria-hidden>{method === "cents_per_km" ? "✓" : ""}</span></span>
              <span className="muted small">A set rate for each work km, up to 5,000 km a year. No receipts needed, just a record of your work km.</span>
            </button>
          </div>
          <p className="muted small">You can choose differently for each year later.</p>
        </>
      ),
    },
    {
      title: "Its value when you started here",
      valid: !needsOpening || Boolean(opening.trim()),
      body: needsOpening ? (
        <>
          <p className="muted">This car was bought before the years TaxApp has car rules for. Enter its <strong>adjustable value</strong> (its value for tax after depreciation) at {shortDate(needsOpening.asAt)}. It's on the depreciation schedule in your last tax return; your accountant can give it to you.</p>
          <div className="field" style={{ maxWidth: 260 }}><label htmlFor="car-open">Value at {shortDate(needsOpening.asAt)}</label><input id="car-open" type="text" inputMode="decimal" value={opening} onChange={(e) => setOpening(e.target.value)} placeholder="12000" /></div>
        </>
      ) : <p className="muted">Ready to add.</p>,
    },
  ];
  const last = needsOpening ? 3 : 2;
  const current = steps[step]!;

  return (
    <div className="wizard stack" style={{ "--gap": "24px" } as React.CSSProperties}>
      <div className="row"><Link to="/cars" className="btn ghost">← Cars</Link><div className="spacer" /><span className="muted small">Step {step + 1} of {last + 1}</span></div>
      <div className="progress" aria-hidden><div style={{ width: `${((step + 1) / (last + 1)) * 100}%` }} /></div>
      <h1>{current.title}</h1>
      <div className="stack" style={{ "--gap": "16px" } as React.CSSProperties}>{current.body}</div>
      <ErrorNotice error={error} />
      <div className="wizard-actions">
        {step > 0 && <button type="button" className="btn ghost" onClick={() => setStep(step - 1)} disabled={busy}>Back</button>}
        <div className="spacer" />
        {step < last
          ? <button type="button" className="btn primary" disabled={!current.valid} onClick={() => setStep(step + 1)}>Continue</button>
          : <button type="button" className="btn primary" disabled={!current.valid || busy} onClick={save}>{busy ? "Adding…" : "Add car"}</button>}
      </div>
    </div>
  );
}

// ---- One car ----------------------------------------------------------------------------------

function YearCard({ car, y, onChange }: { car: VehicleDetail; y: CarYear; onChange: (car: VehicleDetail) => void }) {
  const [km, setKm] = useState(y.businessKm?.toString() ?? "");
  const [error, setError] = useState<unknown>(null);
  const setMethod = async (method: CarMethod, businessKm?: number | null) => {
    try {
      setError(null);
      onChange(await api.put<VehicleDetail>(`/api/vehicles/${car.id}/years/${y.fy}`, { method, businessKm: businessKm ?? null }));
    } catch (e) {
      setError(e);
    }
  };
  return (
    <div className="card stack" style={{ "--gap": "12px" } as React.CSSProperties}>
      <div className="row wrap">
        <h3>{y.label}</h3>
        {y.daysHeld < 365 && y.daysHeld > 0 && <span className="pill">{y.daysHeld} days owned</span>}
        <div className="spacer" />
        <Choice label={`Method for ${y.label}`} value={y.methodChosen ? y.method : null} onChange={(m) => setMethod(m, m === "cents_per_km" ? int(km) : null)}
          options={[{ value: "logbook", label: "Logbook" }, { value: "cents_per_km", label: "Cents per km" }]} />
      </div>
      {y.method === "cents_per_km" && (
        <div className="row" style={{ maxWidth: 360 }}>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor={`km-${y.fy}`}>Work kilometres this year</label>
            <input id={`km-${y.fy}`} type="number" min={0} value={km} onChange={(e) => setKm(e.target.value)} onBlur={() => int(km) !== y.businessKm && setMethod("cents_per_km", int(km))} />
          </div>
        </div>
      )}
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        {y.method === "logbook" && <div className="stat"><span className="muted small">Business use</span><strong>{y.businessPct === null ? "—" : `${y.businessPct}%`}</strong></div>}
        {y.method === "logbook" && <div className="stat"><span className="muted small">Running costs</span><strong>{money(y.runningCosts)}</strong><span className="muted small">{y.receiptCount} receipt{y.receiptCount === 1 ? "" : "s"}</span></div>}
        {y.decline && <div className="stat"><span className="muted small">Depreciation</span><strong>{money(y.decline.decline)}</strong><span className="muted small">Value at end {money(y.decline.closing)}</span></div>}
        <div className="stat"><span className="muted small">Claim</span><strong style={{ color: "var(--accent)" }}>{y.claim ? money(y.claim.cents) : "—"}</strong></div>
      </div>
      {y.claim && <p className="muted small">{y.claim.explanation}</p>}
      {y.decline && <details className="small muted"><summary>How depreciation was worked out</summary>{y.decline.explanation}</details>}
      {y.checks.map((c, i) => <div key={i} className={`notice${c.level === "warning" ? " warn" : ""}`}>{c.message}</div>)}
      <ErrorNotice error={error} />
    </div>
  );
}

export function CarDetail() {
  const { id } = useParams();
  const [car, setCar] = useState<VehicleDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    api.get<VehicleDetail>(`/api/vehicles/${id}`).then(setCar).catch(setError);
  }, [id]);
  if (!car) return <><ErrorNotice error={error} /><Loading /></>;

  const ba = car.sale?.balancingAdjustment;
  return (
    <div className="stack" style={{ "--gap": "24px" } as React.CSSProperties}>
      <div className="row wrap">
        <Link to="/cars" className="btn ghost">← Cars</Link>
      </div>
      <div className="row wrap">
        <div>
          <h1>{car.name}</h1>
          <p className="muted">
            {[car.year, car.make, car.model].filter(Boolean).join(" ") || "Car"}{car.registration ? ` · ${car.registration}` : ""} ·
            {" "}got {shortDate(car.acquiredOn)} for {money(car.costCents)}
            {car.sale && ` · sold ${shortDate(car.sale.soldOn)} for ${money(car.sale.priceCents)}`}
          </p>
        </div>
        <div className="spacer" />
        {car.currentBusinessPct !== null && <div className="stat" style={{ textAlign: "right" }}><span className="muted small">Business use now</span><strong style={{ fontSize: 24 }}>{car.currentBusinessPct}%</strong></div>}
      </div>

      {car.depreciation.problem && <div className="notice warn">{car.depreciation.problem}</div>}
      {car.depreciation.carLimit?.applied && (
        <div className="notice">Its cost is above the {car.depreciation.carLimit.fy.replace("FY", "")} car limit, so depreciation is worked out on {money(car.depreciation.carLimit.limit)}.</div>
      )}

      {car.sale && ba && (
        <section className="card stack" style={{ "--gap": "8px" } as React.CSSProperties}>
          <h2>Sale</h2>
          {ba.status === "calculated" && <p><strong>{money(ba.amount)}</strong> {ba.kind === "assessable" ? "to include as income (item 24)" : "to claim as a deduction (D5)"}.</p>}
          {ba.status === "none" && <p>No balancing adjustment.</p>}
          {ba.status === "refer" && <div className="notice warn">Ask your accountant to work out the balancing adjustment. The figures they need are in your year-end pack.</div>}
          <p className="muted small">{ba.explanation}</p>
        </section>
      )}

      <section className="stack">
        <h2>Year by year</h2>
        {[...car.years].reverse().map((y) => <YearCard key={y.fy} car={car} y={y} onChange={setCar} />)}
      </section>

      <section className="stack">
        <div className="row"><h2>Logbooks</h2></div>
        {car.logbooks.length === 0 ? <p className="muted">No logbook yet. Keep one for 12 continuous weeks; its work-use % then applies until you keep a new one (at least every 5 years).</p> : (
          <div className="card list-card list">
            {car.logbooks.map((l) => (
              <div key={l.id} className="list-item">
                <div style={{ flex: 1 }}>{shortDate(l.startsOn)} – {shortDate(l.endsOn)}<div className="muted small">{(l.odometerEnd - l.odometerStart).toLocaleString("en-AU")} km, {l.businessKm.toLocaleString("en-AU")} for work</div></div>
                <strong>{l.businessPct}%</strong>
              </div>
            ))}
          </div>
        )}
        {!car.sale && (
          <InlineForm<VehicleDetail> title="Add a logbook" onDone={setCar}
            fields={[
              { id: "startsOn", label: "First day", type: "date" }, { id: "endsOn", label: "Last day", type: "date", hint: "At least 12 weeks after the first day" },
              { id: "odometerStart", label: "Odometer at start", type: "number" }, { id: "odometerEnd", label: "Odometer at end", type: "number" },
              { id: "businessKm", label: "Work km in that period", type: "number" },
            ]}
            submit={(v) => api.post(`/api/vehicles/${car.id}/logbooks`, {
              startsOn: v.startsOn, endsOn: v.endsOn, odometerStart: Number(v.odometerStart), odometerEnd: Number(v.odometerEnd), businessKm: Number(v.businessKm),
            })} />
        )}
        {car.businessUseHistory.length > 1 && (
          <p className="muted small">Business use over time: {car.businessUseHistory.map((h) => `${h.pct}% from ${shortDate(h.from)}${h.to ? ` to ${shortDate(h.to)}` : ""}`).join("; ")}.</p>
        )}
      </section>

      <section className="stack">
        <h2>Odometer</h2>
        <p className="muted small">For the logbook method, record it at the start and end of each financial year (1 July and 30 June).</p>
        {car.odometer.length > 0 && (
          <div className="card list-card list">
            {[...car.odometer].reverse().map((o) => <div key={o.readOn} className="list-item"><span style={{ flex: 1 }}>{shortDate(o.readOn)}</span><span className="amount">{o.reading.toLocaleString("en-AU")} km</span></div>)}
          </div>
        )}
        {!car.sale && (
          <InlineForm<VehicleDetail> title="Record odometer" onDone={setCar}
            fields={[{ id: "readOn", label: "Date", type: "date" }, { id: "reading", label: "Reading (km)", type: "number" }]}
            submit={(v) => api.post(`/api/vehicles/${car.id}/odometer`, { readOn: v.readOn, reading: Number(v.reading) })} />
        )}
      </section>

      <section className="stack">
        <h2>Depreciation</h2>
        <div className="card flat stack" style={{ "--gap": "6px" } as React.CSSProperties}>
          <div className="row"><span className="muted">Method</span><div className="spacer" /><strong>{car.depreciation.method === "diminishing_value" ? "Diminishing value" : "Prime cost"}</strong></div>
          <div className="row"><span className="muted">Effective life</span><div className="spacer" /><strong>{car.depreciation.effectiveLifeYears} years</strong></div>
          {car.depreciation.depreciableCost !== null && <div className="row"><span className="muted">Cost for depreciation</span><div className="spacer" /><strong>{money(car.depreciation.depreciableCost)}</strong></div>}
          {car.depreciation.opening && <div className="row"><span className="muted">Value at {shortDate(car.depreciation.opening.asAt)}</span><div className="spacer" /><strong>{money(car.depreciation.opening.valueCents)}</strong></div>}
          <p className="muted small">8 years is the usual effective life for a car; check it with your accountant.</p>
        </div>
      </section>

      {!car.sale && (
        <section className="stack">
          <h2>Sold it?</h2>
          <InlineForm<VehicleDetail> title="Record the sale" onDone={setCar}
            fields={[{ id: "soldOn", label: "Date sold", type: "date" }, { id: "price", label: "Sale price", type: "text" }, { id: "odometer", label: "Odometer at sale", type: "number" }]}
            submit={(v) => {
              const cents = toCents(v.price ?? "");
              if (cents === null || Number.isNaN(cents)) throw new Error("Enter the sale price, like 25000");
              return api.post(`/api/vehicles/${car.id}/sell`, { soldOn: v.soldOn, salePriceCents: cents, odometer: int(v.odometer ?? "") });
            }} />
        </section>
      )}
    </div>
  );
}
